import { Calendar, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

interface DateRangePickerProps {
	onChange: (startDate: Date | null, endDate: Date | null) => void;
	clearable?: boolean;
}

const DateRangePicker: React.FC<DateRangePickerProps> = ({
	onChange,
	clearable = false,
}) => {
	const [startDate, setStartDate] = useState<Date | null>(null);
	const [endDate, setEndDate] = useState<Date | null>(null);
	const [tempStartDate, setTempStartDate] = useState<string>('');
	const [tempEndDate, setTempEndDate] = useState<string>('');
	const [showCalendar, setShowCalendar] = useState<'start' | 'end' | null>(
		null,
	);
	const [currentMonth, setCurrentMonth] = useState<number>(
		new Date().getMonth(),
	);
	const [currentYear, setCurrentYear] = useState<number>(
		new Date().getFullYear(),
	);
	const calendarRef = useRef<HTMLDivElement>(null);
	const startInputRef = useRef<HTMLDivElement>(null);
	const endInputRef = useRef<HTMLDivElement>(null);

	function formatDateDisplay(date: Date): string {
		const day = date.getDate().toString().padStart(2, '0');
		const month = (date.getMonth() + 1).toString().padStart(2, '0');
		const year = date.getFullYear();
		return `${day}/${month}/${year}`;
	}

	function parseDateInput(dateString: string): Date | null {
		const parts = dateString.split('/');
		if (parts.length === 3) {
			const day = parseInt(parts[0]);
			const month = parseInt(parts[1]) - 1;
			const year = parseInt(parts[2]);
			const date = new Date(year, month, day);
			if (!isNaN(date.getTime())) {
				return date;
			}
		}
		return null;
	}

	const clearDates = () => {
		setStartDate(null);
		setEndDate(null);
		setTempStartDate('');
		setTempEndDate('');
		onChange(null, null);
	};

	const handleDateSelect = (date: Date) => {
		if (showCalendar === 'start') {
			const newDate = new Date(date);
			newDate.setHours(0, 0, 0, 0);
			setStartDate(newDate);
			setTempStartDate(formatDateDisplay(newDate));
			onChange(newDate, endDate);
		} else if (showCalendar === 'end') {
			const newDate = new Date(date);
			newDate.setHours(23, 59, 59, 999);
			setEndDate(newDate);
			setTempEndDate(formatDateDisplay(newDate));
			onChange(startDate, newDate);
		}
		setShowCalendar(null);
	};

	const handleInputChange = (type: 'start' | 'end', value: string) => {
		const date = parseDateInput(value);

		if (type === 'start') {
			setTempStartDate(value);
			if (date) {
				const newDate = new Date(date);
				newDate.setHours(0, 0, 0, 0);
				setStartDate(newDate);
				onChange(newDate, endDate);
			} else {
				setStartDate(null);
				onChange(null, endDate);
			}
		} else {
			setTempEndDate(value);
			if (date) {
				const newDate = new Date(date);
				newDate.setHours(23, 59, 59, 999);
				setEndDate(newDate);
				onChange(startDate, newDate);
			} else {
				setEndDate(null);
				onChange(startDate, null);
			}
		}
	};

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				calendarRef.current &&
				!calendarRef.current.contains(event.target as Node)
			) {
				setShowCalendar(null);
			}
		};

		if (showCalendar) {
			document.addEventListener('mousedown', handleClickOutside);
		} else {
			document.removeEventListener('mousedown', handleClickOutside);
		}

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [showCalendar]);

	const generateDays = () => {
		const firstDayOfMonth = new Date(currentYear, currentMonth, 1);
		const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0);
		const daysInMonth = lastDayOfMonth.getDate();
		const startingDayOfWeek = firstDayOfMonth.getDay();

		const days = [];

		for (let i = 0; i < startingDayOfWeek; i++) {
			days.push(<div key={`empty-${i}`} className="h-10 w-10"></div>);
		}

		const today = new Date();
		today.setHours(0, 0, 0, 0);

		for (let day = 1; day <= daysInMonth; day++) {
			const date = new Date(currentYear, currentMonth, day);
			const isToday = date.toDateString() === today.toDateString();
			const isStart =
				startDate && date.toDateString() === startDate.toDateString();
			const isEnd =
				endDate && date.toDateString() === endDate.toDateString();
			const isInRange =
				startDate && endDate && date > startDate && date < endDate;
			const isDisabled =
				showCalendar === 'end' && startDate && date < startDate;

			let className =
				'md:size-6 size-5 flex items-center justify-center self-center justify-self-center rounded-full cursor-pointer text-sm text-stone-600';
			if (isToday) className += ' border border-brand-purple';
			if (isStart) className += ' bg-brand-purple !text-white';
			if (isEnd) className += ' bg-brand-purple !text-white';
			if (isInRange) className += ' bg-brand-purple !text-white';
			if (isDisabled) className += ' text-gray-300 cursor-not-allowed';

			days.push(
				<div
					key={`day-${day}`}
					className={className}
					onClick={() => !isDisabled && handleDateSelect(date)}
				>
					{day}
				</div>,
			);
		}

		return days;
	};

	const changeMonth = (increment: number) => {
		let newMonth = currentMonth + increment;
		let newYear = currentYear;

		if (newMonth < 0) {
			newMonth = 11;
			newYear--;
		} else if (newMonth > 11) {
			newMonth = 0;
			newYear++;
		}

		setCurrentMonth(newMonth);
		setCurrentYear(newYear);
	};

	const monthNames = [
		'Janeiro',
		'Fevereiro',
		'Março',
		'Abril',
		'Maio',
		'Junho',
		'Julho',
		'Agosto',
		'Setembro',
		'Outubro',
		'Novembro',
		'Dezembro',
	];

	const dayNames = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

	useEffect(() => {
		if (showCalendar) {
			const dateToShow =
				showCalendar === 'start'
					? startDate || new Date()
					: endDate || new Date();
			setCurrentMonth(dateToShow.getMonth());
			setCurrentYear(dateToShow.getFullYear());
		}
	}, [showCalendar, startDate, endDate]);

	return (
		<div className="relative w-full">
			<div className="flex flex-col items-center gap-3 md:flex-row md:items-center md:gap-3">
				<div className="relative w-full md:w-auto" ref={startInputRef}>
					<div className="relative flex w-full items-center rounded-xl border border-stone-200 bg-white px-3 py-2 shadow-sm md:w-auto md:min-w-[190px]">
						<input
							type="text"
							className="w-full cursor-pointer rounded-md bg-transparent text-sm text-stone-600 placeholder:text-xs placeholder:text-stone-400 focus:outline-none"
							value={tempStartDate}
							onChange={(e) =>
								handleInputChange('start', e.target.value)
							}
							onClick={() => setShowCalendar('start')}
							placeholder="DD/MM/AAAA"
						/>
						<Calendar
							className="size-4 cursor-pointer text-stone-500"
							onClick={() => setShowCalendar('start')}
						/>
					</div>
					{showCalendar === 'start' && (
						<div
							ref={calendarRef}
							className="z-10 mt-1 w-full max-w-md rounded-lg border border-stone-200 bg-white p-2 md:absolute md:min-w-max md:p-4 md:shadow-lg"
						>
							<div className="mb-4">
								<div className="flex items-center justify-between">
									<button
										onClick={() => changeMonth(-1)}
										className="rounded p-2 hover:bg-gray-100"
									>
										<ChevronLeft className="size-4 text-stone-600" />
									</button>
									<h3 className="px-4 font-medium text-stone-600">
										{monthNames[currentMonth]} {currentYear}
									</h3>
									<button
										onClick={() => changeMonth(1)}
										className="rounded p-2 hover:bg-gray-100"
									>
										<ChevronRight className="size-4 text-stone-600" />
									</button>
								</div>

								<div className="grid grid-cols-7 text-center text-sm">
									{dayNames.map((day) => (
										<div
											key={day}
											className="flex items-center justify-center font-medium text-stone-500"
										>
											{day}
										</div>
									))}
								</div>

								<div className="grid grid-cols-7 gap-1">
									{generateDays()}
								</div>
							</div>
						</div>
					)}
				</div>
				<span className="text-sm text-stone-800 md:block">Até</span>
				<div className="relative w-full md:w-auto" ref={endInputRef}>
					<div className="flex w-full gap-2 md:w-auto">
						<div className="relative flex w-full items-center rounded-xl border border-stone-200 bg-white px-3 py-2 shadow-sm md:w-auto md:min-w-[190px]">
							<input
								type="text"
								className="w-full cursor-pointer rounded-md bg-transparent text-sm text-stone-600 placeholder:text-xs placeholder:text-stone-400 focus:outline-none"
								value={tempEndDate}
								onChange={(e) =>
									handleInputChange('end', e.target.value)
								}
								onClick={() => setShowCalendar('end')}
								placeholder="DD/MM/AAAA"
							/>
							<Calendar
								className="size-4 cursor-pointer text-stone-500"
								onClick={() => setShowCalendar('end')}
							/>
						</div>
						{clearable && (startDate || endDate) && (
							<button
								onClick={clearDates}
								className="rounded-full p-2 text-stone-500 hover:bg-gray-100"
								title="Limpar datas"
							>
								<X className="size-4" />
							</button>
						)}
					</div>
					{showCalendar === 'end' && (
						<div
							ref={calendarRef}
							className="z-10 mt-1 w-full max-w-md rounded-lg border border-stone-200 bg-white p-2 md:absolute md:min-w-max md:p-4 md:shadow-lg"
						>
							<div className="mb-4">
								<div className="flex items-center justify-between">
									<button
										onClick={() => changeMonth(-1)}
										className="rounded p-2 hover:bg-gray-100"
									>
										<ChevronLeft className="size-4 text-stone-600" />
									</button>
									<h3 className="px-4 font-medium text-stone-600">
										{monthNames[currentMonth]} {currentYear}
									</h3>
									<button
										onClick={() => changeMonth(1)}
										className="rounded p-2 hover:bg-gray-100"
									>
										<ChevronRight className="size-4 text-stone-600" />
									</button>
								</div>

								<div className="grid grid-cols-7 text-center text-sm">
									{dayNames.map((day) => (
										<div
											key={day}
											className="flex h-8 w-10 items-center justify-center font-medium text-stone-500"
										>
											{day}
										</div>
									))}
								</div>

								<div className="grid grid-cols-7 gap-1">
									{generateDays()}
								</div>
							</div>
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

export default DateRangePicker;
